import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Form, Input, Space, Tag, Typography } from 'antd';
import { IconBuilding, IconFileText } from '@tabler/icons-react';
import { CrudMode, CrudSchemaFields, renderListToTag, renderSwitchToTag, renderMultiListToTags, type EnsoFormItem } from '@ohpen/enso-ui';
import { ExportOutlined } from '@ant-design/icons';

import { FieldType, FirmType, ActivityType } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { getHighlightProps } from '../utils';
import styles from '../styles.module.css';

export const RegistrationDetails: React.FC<TabProps> = ({
  form,
  mode,
  selectsOptions,
  populateFcaData,
  fcaModifiedFields,
  removeFcaModifiedField,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const readonly = mode === CrudMode.VIEW;
  const watchedDisabledActivities = Form.useWatch(['registrationDetails', 'disabledActivitiesPermissions'], form);
  const frn = Form.useWatch(['registrationDetails', 'frn'], form);
  const type = Form.useWatch(['registrationDetails', 'type'], form);
  const name = Form.useWatch(['registrationDetails', 'name'], form);

  const disabledActivitiesPermissions = useMemo(() => watchedDisabledActivities ?? [], [watchedDisabledActivities]);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  useEffect(() => {
    if (mode !== CrudMode.CREATE) return;
    if (isFcaRegistered && frn) {
      form?.setFieldValue(['registrationDetails', 'ohpenId'], `FRM_${frn}`);
    } else if (!isFcaRegistered && name) {
      const sanitized = name.replace(/[^A-Za-z0-9_-]/g, '_');
      form?.setFieldValue(['registrationDetails', 'ohpenId'], `FRM_${sanitized}`);
    }
  }, [mode, isFcaRegistered, frn, name, form]);

  const createActivities = useMemo(() => {
    const opts = [
      { label: t('lender.firms.labels.btl'), value: ActivityType.CREATE_BUY_TO_LET_CASES, disabled: disabledActivitiesPermissions.includes(ActivityType.CREATE_BUY_TO_LET_CASES) },
      { label: t('lender.firms.labels.cbtl'), value: ActivityType.CREATE_CONSUMER_BUY_TO_LET_CASES, disabled: disabledActivitiesPermissions.includes(ActivityType.CREATE_CONSUMER_BUY_TO_LET_CASES) },
      { label: t('lender.firms.labels.rmc'), value: ActivityType.CREATE_REGULATED_MORTGAGE_CONTRACT_CASES, disabled: disabledActivitiesPermissions.includes(ActivityType.CREATE_REGULATED_MORTGAGE_CONTRACT_CASES) },
    ];
    if (!isFcaRegistered) return opts.filter(o => o.value === ActivityType.CREATE_BUY_TO_LET_CASES);
    return opts;
  }, [isFcaRegistered, disabledActivitiesPermissions, t]);

  const manageActivities = useMemo(() => {
    const opts = [
      { label: t('lender.firms.labels.btl'), value: ActivityType.BUY_TO_LET_CASES, disabled: disabledActivitiesPermissions.includes(ActivityType.BUY_TO_LET_CASES) },
      { label: t('lender.firms.labels.cbtl'), value: ActivityType.CONSUMER_BUY_TO_LET_CASES, disabled: disabledActivitiesPermissions.includes(ActivityType.CONSUMER_BUY_TO_LET_CASES) },
      { label: t('lender.firms.labels.rmc'), value: ActivityType.REGULATED_MORTGAGE_CONTRACT_CASES, disabled: disabledActivitiesPermissions.includes(ActivityType.REGULATED_MORTGAGE_CONTRACT_CASES) },
    ];
    if (!isFcaRegistered) return opts.filter(o => o.value === ActivityType.BUY_TO_LET_CASES);
    return opts;
  }, [isFcaRegistered, disabledActivitiesPermissions, t]);

  const populateData = useCallback(() => {
    setLoading(true);
    if (populateFcaData) {
      populateFcaData(frn).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [frn, populateFcaData]);

  const firmColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.firms.labels.currentStatus'),
        dataIndex: ['registrationDetails', 'isActive'],
        valueType: 'switch',
        initialValue: mode === CrudMode.CREATE ? false : undefined,
        readonly,
        fieldProps: readonly
          ? undefined
          : {
              checkedChildren: t('lender.firms.labels.active'),
              unCheckedChildren: t('lender.firms.labels.inactive'),
            },
        render: renderSwitchToTag,
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.type'),
            dataIndex: ['registrationDetails', 'type'],
            valueType: 'select',
            fieldProps: {
              options: selectsOptions[FieldType.FIRM_TYPE],
              tagColor: 'blue',
              className: getHighlightProps(['registrationDetails', 'type'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'type'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.type')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              rules: [{ required: true }],
            },
            readonly,
            render: renderListToTag,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.principalFirm'),
            dataIndex: ['registrationDetails', 'principalIds'],
            valueType: 'select',
            fieldProps: {
              options: selectsOptions[FieldType.PRINCIPALS],
              tagColor: 'blue',
              mode: 'multiple',
            },
            formItemProps: {
              rules: [{ required: true }],
              hidden: type !== FirmType.APPOINTED_REPRESENTATIVE,
            },
            readonly,
            render: renderMultiListToTags,
            colProps: { span: 12 },
          },
        ],
      },
      // {
      //   title: t('lender.firms.labels.currentStatus'),
      //   dataIndex: ['registrationDetails', 'currentStatus'],
      //   valueType: 'checkbox',
      //   fieldProps: {
      //     options: [{ label: t('lender.firms.labels.firmActive'), value: 'firmActive' }],
      //   },
      //   colProps: { span: 24 },
      // },
      {
        valueType: 'group',
        colProps: { span: 24 },
        hideInForm: !isFcaRegistered,
        columns: [
          {
            title: t('lender.firms.labels.frn'),
            dataIndex: ['registrationDetails', 'frn'],
            valueType: 'text',
            readonly,
            renderText: val => String(val),
            renderFormItem: () => (
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['registrationDetails', 'frn']} noStyle>
                  <Input style={{ width: '100%' }} inputMode="numeric" disabled={mode !== CrudMode.CREATE} />
                </Form.Item>
                <Button type="primary" onClick={populateData} disabled={readonly} loading={loading}>
                  {t('lender.firms.buttons.populateData')}
                </Button>
              </Space.Compact>
            ),
            formItemProps: {
              tooltip: readonly ? null : t('lender.firms.labels.frnPopulateTooltip'),
              rules: [{ required: isFcaRegistered }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.fcaRegister'),
            dataIndex: ['registrationDetails', 'fcaRegisterLink'],
            key: 'fcaRegisterLink',
            valueType: 'text',
            colProps: { span: 12 },

            renderFormItem: () => {
              const frn = form?.getFieldValue(['registrationDetails', 'frn']);
              if (!frn) return '-';

              const url = `https://register.fca.org.uk/s/search?q=${encodeURIComponent(frn)}&type=Companies`;

              return (
                <a href={url} target="_blank" rel="noreferrer">
                  {t('lender.firms.labels.goToWebsite')}
                  <ExportOutlined />
                </a>
              );
            },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.name'),
            dataIndex: ['registrationDetails', 'name'],
            valueType: 'text',
            readonly,
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.name')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              rules: [{ required: true }],
            },
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'name'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'name'),
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.ohpenId'),
            dataIndex: ['registrationDetails', 'ohpenId'],
            valueType: 'text',
            readonly,
            formItemProps: {
              tooltip: readonly ? null : t('lender.firms.labels.ohpenIdTooltip'),
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.reference'),
            dataIndex: ['registrationDetails', 'reference'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
            formItemProps: {
              tooltip: readonly ? null : t('lender.firms.labels.referenceTooltip'),
            },
          },
        ],
      },
    ],
    [t, populateData, readonly, mode, type, form, loading, selectsOptions, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered],
  );

  const registrationColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            dataIndex: ['registrationDetails', 'fcaStatus'],
            valueType: 'text',
            readonly,
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'fcaStatus'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'fcaStatus'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.fcaRegisterStatus')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.nacfbRegistration'),
            dataIndex: ['registrationDetails', 'nacfbNumber'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.operations'),
            dataIndex: ['registrationDetails', 'operations'],
            valueType: 'checkbox',
            fieldProps: {
              options: [
                { label: t('lender.firms.labels.givesAdvice'), value: 'ADVICE' },
                { label: t('lender.firms.labels.packaging'), value: 'PACKAGE' },
                { label: t('lender.firms.labels.actAsPrincipal'), value: 'NETWORK', disabled: type !== FirmType.DIRECTLY_AUTHORISED },
                { label: t('lender.firms.labels.mortgageClub'), value: 'MORTGAGE_CLUB' },
              ],
              disabled: readonly,
              style: {
                display: 'flex',
                flexDirection: 'column',
                rowGap: 4,
              },
            },
            formItemProps: {
              label: t('lender.firms.labels.operations'),
            },
            colProps: { span: 12 },
          },
          {
            dataIndex: ['registrationDetails', 'activitiesPermissions'],
            valueType: 'checkbox',
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.activitiesPermissions')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            renderFormItem: (_schema, _config, formInstance) => {
              const current: ActivityType[] = formInstance?.getFieldValue(['registrationDetails', 'activitiesPermissions']) ?? [];
              const toggle = (val: ActivityType, checked: boolean) => {
                const next = checked ? [...current, val] : current.filter(v => v !== val);
                formInstance?.setFieldValue(['registrationDetails', 'activitiesPermissions'], next);
              };
              return (
                <div
                  className={`${styles.activitiesPermissions} ${getHighlightProps(['registrationDetails', 'activitiesPermissions'], fcaModifiedFields)}`}
                  onBlur={() => removeFcaModifiedField?.('registrationDetails', 'activitiesPermissions')}
                >
                  <div className={styles.activitySection}>
                    <Typography.Text strong className={styles.activitySectionTitle}>{t('lender.firms.labels.manageCases')}</Typography.Text>
                    <div className={styles.activityCheckboxRow}>
                      {manageActivities.map(opt => (
                        <Checkbox key={opt.value} checked={current.includes(opt.value)} disabled={readonly || opt.disabled} onChange={e => toggle(opt.value, e.target.checked)}>
                          {opt.label}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                  <div className={styles.activitySection}>
                    <Typography.Text strong className={styles.activitySectionTitle}>{t('lender.firms.labels.createCases')}</Typography.Text>
                    <div className={styles.activityCheckboxRow}>
                      {createActivities.map(opt => (
                        <Checkbox key={opt.value} checked={current.includes(opt.value)} disabled={readonly || opt.disabled} onChange={e => toggle(opt.value, e.target.checked)}>
                          {opt.label}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                </div>
              );
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [createActivities, manageActivities, readonly, t, type, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered],
  );

  return (
    <>
      <CrudFormCard icon={<IconBuilding />} title={t('lender.firms.labels.firmTitle')}>
        <CrudSchemaFields schema={firmColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconFileText />} title={t('lender.firms.labels.registrationDetails')}>
        <CrudSchemaFields schema={registrationColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
    </>
  );
};
